import React, { useState } from 'react';
import BaseTruckScheduleForm from '../../components/form/BaseTruckScheduleForm';
import { TextField } from '../../components/form/FormFields';

/**
 * Form component for scheduling inbound trucks
 */
const InboundScheduleForm = ({ onClose, onSave, preSelectedDate, isLoading, truckData }) => {
  // State for form data
  const [formData, setFormData] = useState({
    business_unit: '',
    carrier: '',
    load_type: 'Live Unload',
    size: 1,
    po: '', // Optional field per database schema
    vendor: '', // New field for vendor
    scheduled_date: preSelectedDate || '',
    scheduled_time: '09:00',
    dock_door: '',
    secure: false,
    scheduler_notes: '',
    status: 'Scheduled',
    load_material: '' // New field for load material
  });
  
  // State for form errors
  const [errors, setErrors] = useState({});
  
  // Handle change in custom fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Update our local form state
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Load types specific to inbound
  const loadTypes = [
    'Live Unload',
    'Live Container Unload - 20\'',
    'Live Container Unload - 40\'',
    'Container Drop',
    'Container Drop - 20\'',
    'Container Drop - 40\''
  ];
  
  // Load material types
  const loadMaterialTypes = ['Pallet', 'Oversized Pallet', 'Gaylord', 'Small Parcel', 'Various'];
  
  // Custom validation for inbound trucks
  const validateForm = (formData) => {
    const errors = {};
    
    if (!formData.business_unit) {
      errors.business_unit = 'Business unit is required';
    }
    
    if (!formData.carrier) {
      errors.carrier = 'Carrier is required';
    }
    
    if (!formData.load_type) {
      errors.load_type = 'Load type is required';
    }
    
    if (!formData.size || formData.size < 1) {
      errors.size = 'Size must be at least 1';
    }
    
    if (!formData.scheduled_date) {
      errors.scheduled_date = 'Scheduled date is required';
    }
    
    if (!formData.scheduled_time) {
      errors.scheduled_time = 'Scheduled time is required';
    }
    
    // PO field is optional according to database schema
    // No validation needed for this field - it can be null/empty
    
    return errors;
  };
  
  // Submit handler to hook into BaseTruckScheduleForm
  const handleSubmit = async (truckData) => {
    // Add the PO field, vendor field, and load_material field from our local form state to ensure they're included
    // This is critical since the custom fields maintain their own state
    const dataWithFields = {
      ...truckData,
      po: formData.po, // Explicitly add PO from our local state
      vendor: formData.vendor, // Explicitly add vendor from our local state
      load_material: formData.load_material, // Explicitly add load_material from our local state
      status: 'Scheduled' // Ensure status is set for the server
    };
    
    console.log('InboundScheduleForm: Submitting with PO, vendor, and load_material included from local state', {
      originalPO: truckData.po,
      localStatePO: formData.po,
      originalVendor: truckData.vendor,
      localStateVendor: formData.vendor,
      originalLoadMaterial: truckData.load_material,
      localStateLoadMaterial: formData.load_material,
      finalPO: dataWithFields.po,
      finalVendor: dataWithFields.vendor,
      finalLoadMaterial: dataWithFields.load_material
    });
    
    // Create data object with validated fields for API
    const result = await onSave(dataWithFields);
    
    // Check if result contains validation errors
    if (result && typeof result === 'object' && result.errors) {
      // Update the local error state
      setErrors(result.errors);
      return false;
    }
    return result;
  };
  
  // Handle load material change
  const handleLoadMaterialChange = (e) => {
    const { value } = e.target;
    
    // Update local state
    handleChange(e);
    
    // Also update the parent form state
    if (window.truckScheduleFormSetData) {
      window.truckScheduleFormSetData(prev => ({
        ...prev,
        load_material: value
      }));
    }
    
    // Log for debugging
    console.log('Load material field changed:', value);
  };
  
  // Override BaseTruckScheduleForm's handleChange for load_material
  React.useEffect(() => {
    // Listen for changes to the load_material field in BaseTruckScheduleForm
    const loadMaterialField = document.getElementById('load_material');
    if (loadMaterialField) {
      const originalOnChange = loadMaterialField.onchange;
      loadMaterialField.onchange = (e) => {
        if (originalOnChange) originalOnChange(e);
        handleLoadMaterialChange(e);
      };
    }
    
    // Cleanup
    return () => {
      const field = document.getElementById('load_material');
      if (field) {
        field.onchange = null;
      }
    };
  }, []);
  
  // Custom fields specific to inbound trucks
  const customFields = (
    <>
      <TextField
        label="PO Number (Optional)"
        id="po"
        name="po"
        placeholder="Enter PO number if available"
        value={formData.po || ''}
        onChange={(e) => {
          // Update local state
          handleChange(e);
          
          // Also update the parent form state - this is critical for syncing with BaseTruckScheduleForm
          // By doing this, we ensure the PO is included in the displayed formData at all times
          if (window.truckScheduleFormSetData) {
            window.truckScheduleFormSetData(prev => ({
              ...prev,
              po: e.target.value
            }));
          }
          
          // Log for debugging
          console.log('PO field changed:', e.target.value);
        }}
        error={errors.po}
        disabled={isLoading}
      />
      
      <TextField
        label="Vendor (Optional)"
        id="vendor"
        name="vendor"
        placeholder="Enter vendor name"
        value={formData.vendor || ''}
        onChange={(e) => {
          // Update local state
          handleChange(e);
          
          // Also update the parent form state - this is critical for syncing with BaseTruckScheduleForm
          // By doing this, we ensure the vendor is included in the displayed formData at all times
          if (window.truckScheduleFormSetData) {
            window.truckScheduleFormSetData(prev => ({
              ...prev,
              vendor: e.target.value
            }));
          }
          
          // Log for debugging
          console.log('Vendor field changed:', e.target.value);
        }}
        error={errors.vendor}
        disabled={isLoading}
      />
    </>
  );
  
  return (
    <BaseTruckScheduleForm
      onClose={onClose}
      onSave={handleSubmit}
      preSelectedDate={preSelectedDate}
      isLoading={isLoading}
      truckData={truckData}
      initialValues={formData}
      customFields={customFields}
      loadTypes={loadTypes}
      loadMaterialTypes={loadMaterialTypes}
      validateForm={validateForm}
      title="Schedule New Inbound Truck"
    />
  );
};

export default InboundScheduleForm;